import * as types from "./types";
import BridgeAPI from "../../services/bridge";
import * as selectors from "./selectors";
import { getBridgeBrand, getBrandValue } from "../brand/selectors";
import { isAuthenticated } from "../user/selectors";
import { normalize, schema } from "../normalize";
import {
  shouldNextPageFetch,
  getMaxLoadedOrLoadingPage,
  ASSETS,
  ADOPTIONS
} from "./selectors";
import { getBookInfo, loadBookList } from "../books/actions";
import { getBookByIsbn } from "../books/selectors";
import { isUnlimitedUserCredit, activeParams } from "./utils";
import { getAsset } from "./selectors";
import { shouldAutoActivateAsset } from "./selectors";
import { fetchAllFavorites } from "../favorites/actions";
import { favoritesEnabled } from "../favorites/selectors";

const getBridgeOpts = state => {
  let opts = {},
    brand = getBridgeBrand(state);
  if (brand) {
    opts.brand = brand;
  }
  return opts;
};

const fetchSessionRequest = () => ({
  type: types.FETCH_SESSION_REQUEST
});

export const syncFetchSessionSuccess = response => ({
  type: types.SYNC_FETCH_SESSION_SUCCESS,
  response
});

const fetchSessionSuccess = response => ({
  type: types.FETCH_SESSION_SUCCESS,
  response
});

const fetchSessionFailure = error => ({
  type: types.FETCH_SESSION_FAILURE,
  error
});

export const fetchSession = forceUpdate => (dispatch, getState) => {
  const state = getState();
  const bridgeEnabled = getBrandValue(state, "Bridge.enabled", false);
  /* Technically we don't care about calling bridge if not authenticated */
  if (isAuthenticated(state)) {
    dispatch(fetchSessionRequest());
    return BridgeAPI.Session.get(getBridgeOpts(state), forceUpdate).then(
      response => {
        dispatch(fetchSessionSuccess(response));
        return response;
      },
      error => {
        dispatch(fetchSessionFailure(error));
      }
    );
  }
};

export const logout = () => {
  return BridgeAPI.Session.logout();
};

const genericSchema = id =>
  new schema.Entity("content", {}, { idAttribute: id });
const genericListSchema = id => new schema.Array(genericSchema(id));

const defaultListEntities = {
  content: {}
};

const fetchRequest = (type, args) => ({
  type,
  args
});

const fetchSuccess = (type, args, data, metadata) => ({
  type,
  args,
  data,
  metadata
});

const fetchFailure = (type, args, error) => ({
  type,
  args,
  error
});

const shouldFetch = (key, state) => {
  const bridgeEnabled = getBrandValue(state, "Bridge.enabled", false);
  return bridgeEnabled;
};

const fetchGeneric =
  (key, args, request, success, failure, attributeId = "id") =>
  async (dispatch, getState) => {
    const state = getState();
    if (shouldFetch(key, state)) {
      dispatch(fetchRequest(request, args));
      await dispatch(fetchSession());
      return BridgeAPI[key]
        .list({ ...getBridgeOpts(state), ...args })
        .then(response => {
          const { content, ...metadata } = response;
          const normalizedData = normalize(
            content,
            genericListSchema(attributeId),
            defaultListEntities
          );
          dispatch(fetchSuccess(success, args, normalizedData, metadata));
        })
        .catch(error => dispatch(fetchFailure(failure, args, error)));
    }
  };

export const fetchGroups = args =>
  fetchGeneric(
    "Groups",
    args,
    types.FETCH_GROUPS_REQUEST,
    types.FETCH_GROUPS_SUCCESS,
    types.FETCH_GROUPS_FAILURE
  );

export const fetchAdoptions = args =>
  fetchGeneric(
    "Adoptions",
    args,
    types.FETCH_ADOPTIONS_REQUEST,
    types.FETCH_ADOPTIONS_SUCCESS,
    types.FETCH_ADOPTIONS_FAILURE
  );

export const fetchAssets = args =>
  fetchGeneric(
    "Assets",
    args,
    types.FETCH_ASSETS_REQUEST,
    types.FETCH_ASSETS_SUCCESS,
    types.FETCH_ASSETS_FAILURE,
    "vbid"
  );

const createMoreFetcher =
  (fetcher, selectorKey) => args => async (dispatch, getState) => {
    if (shouldNextPageFetch(getState(), selectorKey, args)) {
      const nextPage =
        getMaxLoadedOrLoadingPage(getState(), selectorKey, args) + 1;
      return dispatch(fetcher({ ...args, page: nextPage }));
    }
  };

export const fetchMoreAssets = createMoreFetcher(fetchAssets, ASSETS);
export const fetchMoreAdoptions = createMoreFetcher(fetchAdoptions, ADOPTIONS);

export const fetchLists = lists => async (dispatch, getState) => {
  const state = getState();
  if (lists) {
    lists.forEach(args => {
      dispatch(fetchRequest(types.FETCH_ASSETS_REQUEST, args));
    });
  }

  await dispatch(fetchSession());
  return BridgeAPI.Assets.lists({ ...getBridgeOpts(state), lists })
    .then(response => {
      response.lists.forEach((responseList, i) => {
        const { content, ...metadata } = responseList;
        const emptyObj = { entities: { content: [] }, result: [] };
        const normalizedData =
          responseList.status === 200
            ? normalize(content, genericListSchema("vbid"), defaultListEntities)
            : emptyObj;

        dispatch(
          fetchSuccess(
            types.FETCH_ASSETS_SUCCESS,
            lists[i],
            normalizedData,
            metadata
          )
        )
        if (favoritesEnabled(state)) {
          dispatch(fetchAllFavorites(normalizedData.result))
        }
      });
    })
    .catch(error => {
      if (lists) {
        lists.forEach(args =>
          dispatch(fetchFailure(types.FETCH_ASSETS_FAILURE, args, error))
        );
      }
    });
};

const deactivatePromotionRequest = vbid => ({
  type: types.DEACTIVATE_PROMOTION_REQUEST,
  vbid,
  active: false
});
const deactivatePromotionSuccess = vbid => ({
  type: types.DEACTIVATE_PROMOTION_SUCCESS,
  vbid
});
const deactivatePromotionFailure = (vbid, error) => ({
  type: types.DEACTIVATE_PROMOTION_FAILURE,
  vbid,
  error
});

const activatePromotionRequest = vbid => ({
  type: types.ACTIVATE_PROMOTION_REQUEST,
  vbid,
  active: true
});
const activatePromotionSuccess = vbid => ({
  type: types.ACTIVATE_PROMOTION_SUCCESS,
  vbid
});
const activatePromotionFailure = (vbid, error) => ({
  type: types.ACTIVATE_PROMOTION_FAILURE,
  vbid,
  error
});

export const deactivatePromotion = vbid => (dispatch, getState) => {
  const state = getState();
  const bridgeBrand = getBridgeBrand(state);
  dispatch(deactivatePromotionRequest(vbid));
  return BridgeAPI.Promotions.return({
    vbid,
    active: false,
    brand: bridgeBrand
  })
    .then(() => dispatch(deactivatePromotionSuccess(vbid)))
    .then(() => dispatch(fetchSession(true)))
    .catch(error => dispatch(deactivatePromotionFailure(vbid, error)));
};

export const activatePromotion = vbid => (dispatch, getState) => {
  const state = getState();
  const bridgeBrand = getBridgeBrand(state);
  dispatch(activatePromotionRequest(vbid));
  return BridgeAPI.Promotions.activate({
    vbid,
    active: true,
    brand: bridgeBrand
  })
    .then(() => dispatch(activatePromotionSuccess(vbid)))
    .then(() => dispatch(fetchSession(true)))
    .catch(error => dispatch(activatePromotionFailure(vbid, error)));
};

const activateAssetRequest = (vbid, creditId) => ({
  type: types.ACTIVATE_ASSET_REQUEST,
  vbid,
  credit: creditId
});

export const syncActivateAssetSuccess = (vbid, asset) => ({
  type: types.SYNC_ACTIVATE_ASSET_SUCCESS,
  vbid,
  asset
});

const activateAssetSuccess = vbid => ({
  type: types.ACTIVATE_ASSET_SUCCESS,
  vbid
});

const activateAssetFailure = (vbid, error) => ({
  type: types.ACTIVATE_ASSET_FAILURE,
  vbid,
  error
});

export const activateAsset =
  (vbid, active = true) =>
  (dispatch, getState) => {
    const state = getState();
    const asset = selectors.getAsset(state, vbid);
    const credit = selectors.getBestCreditForAsset(state, vbid);
    const isUnlimitedCredit = isUnlimitedUserCredit(credit);

    if (!asset.active && !asset.activating && credit) {
      dispatch(activateAssetRequest(vbid, credit.id));
      return (
        BridgeAPI.Assets.activate({
          ...getBridgeOpts(state),
          vbid,
          active,
          credit: credit.id
        })
          // Order matters here. We want the newly-licensed book info in the state
          // before it gets added to the book list; otherwise we would have malformed
          // book entry in the list.

          // per BO-5967 - Bridge also assumes a book list has already been loaded. For efficiency
          // we do not load the book list unless you're on the library, so now we need
          // to load it before activation for cases when the user deep links into explore.
          .then(() => dispatch(loadBookList()))
          .then(() => dispatch(getBookInfo([vbid])))
          .then(() => dispatch(activateAssetSuccess(vbid)))
          .then(() => {
            if (!isUnlimitedCredit) {
              dispatch(fetchSession(true));
            }
          })
          .then(() => dispatch(fetchLists([activeParams()])))
          .catch(error => {
            dispatch(activateAssetFailure(vbid, error));
          })
      );
    } else if (!getBookByIsbn(state, vbid)) {
      // Even if bridge says it's active we might not have fetched the license info yet
      return dispatch(getBookInfo([vbid]));
    } else {
      return Promise.resolve();
    }
  };

const returnAssetRequest = vbid => ({
  type: types.RETURN_ASSET_REQUEST,
  vbid
});

export const syncReturnAssetSuccess = (vbid, asset) => ({
  type: types.SYNC_RETURN_ASSET_SUCCESS,
  vbid,
  asset
});

const returnAssetSuccess = (vbid, asset) => ({
  type: types.RETURN_ASSET_SUCCESS,
  vbid,
  asset
});

const returnAssetFailure = (vbid, error) => {
  return {
    type: types.RETURN_ASSET_FAILURE,
    vbid,
    error
  };
};
export const returnAsset = vbid => (dispatch, getState) => {
  const state = getState();
  dispatch(returnAssetRequest(vbid));
  return BridgeAPI.Assets.return({
    ...getBridgeOpts(state),
    vbid
  })
    .then(response => dispatch(returnAssetSuccess(vbid, response.asset)))
    .then(response => dispatch(fetchSession(true)))
    .then(response => dispatch(fetchLists([activeParams()])))
    .catch(error => {
      dispatch(returnAssetFailure(vbid, error));
    });
};

const searchBookStoreRequest = query => ({
  type: types.SEARCH_BOOK_STORE_REQUEST,
  query
});

const searchBookStoreSuccess = (results) => ({
  type: types.SEARCH_BOOK_STORE_SUCCESS,
  results
});

const searchBookStoreComplete = query => ({
  type: types.SEARCH_BOOK_STORE_COMPLETE,
  query
});

const searchBookStoreFailure = (error) => ({
  type: types.SEARCH_BOOK_STORE_FAILURE,
  error
});

export const searchBookStore = query => async(dispatch, getState) => {
  const state = getState();
  const { hasAdmin, hasGroups, hasAssets } = state.bridge.user
  const { hasVstStoreLicense } = state.user.info
  const { storeSearchEnable } = state.brand.data.Bridge

  await dispatch(fetchSession());
  if(!hasAdmin && !hasGroups && !hasAssets && hasVstStoreLicense && storeSearchEnable){
    dispatch(searchBookStoreRequest(query));
    return BridgeAPI.Store.Assets.list({
      ...getBridgeOpts(state),
      page: 1,
      search: query,
      size: 10,
      sort: "default"
    })
    .then(response => {
      dispatch(searchBookStoreSuccess(response.content))
      dispatch(searchBookStoreComplete(query))
    })
    .catch(error => {
      dispatch(searchBookStoreFailure(error))
    });
  }else{
    return Promise.resolve();
  }
}

const fetchSingleAssetRequest = vbid => ({
  type: types.FETCH_SINGLE_ASSET_REQUEST,
  vbid
});
const fetchSingleAssetSuccess = asset => ({
  type: types.FETCH_SINGLE_ASSET_SUCCESS,
  asset
});
const fetchSingleAssetFailure = (vbid, error) => ({
  type: types.FETCH_SINGLE_ASSET_FAILURE,
  vbid,
  error
});

const fetchSingleAsset = vbid => (dispatch, getState) => {
  const state = getState();
  dispatch(fetchSingleAssetRequest(vbid));
  return BridgeAPI.Assets.get({
    ...getBridgeOpts(state),
    vbid
  })
    .then(response => dispatch(fetchSingleAssetSuccess(response)))
    .catch(error => {
      dispatch(fetchSingleAssetFailure(vbid, error));
    });
};

export const tryFetchAndActivate = vbid => async (dispatch, getState) => {
  if (!getAsset(getState(), vbid)) {
    await dispatch(fetchSingleAsset(vbid));
  }
  if (shouldAutoActivateAsset(getState(), vbid)) {
    await dispatch(activateAsset(vbid));
  }
};

export const fetchAncillariesRequest = isbn => ({
  type: types.FETCH_BRIDGE_ANCILLARIES_REQUEST,
  isbn
});

export const fetchAncillariesSuccess = (isbn, data) => ({
  type: types.FETCH_BRIDGE_ANCILLARIES_SUCCESS,
  isbn,
  data
});

export const fetchAncillariesFailure = (isbn, error) => ({
  type: types.FETCH_BRIDGE_ANCILLARIES_FAILURE,
  isbn,
  error
});

export const fetchAncillaries = vbid => async (dispatch, getState) => {
  const state = getState();
  if (selectors.shouldFetchAncillaries(state, vbid)) {
    dispatch(fetchAncillariesRequest(vbid));
    return BridgeAPI.Ancillaries.get({
      ...getBridgeOpts(state),
      vbid
    })
      .then(data => {
        dispatch(fetchAncillariesSuccess(vbid, data));
      })
      .catch(error => dispatch(fetchAncillariesFailure(vbid, error)));
  }
};

export const internal = {};
